import React from 'react'

/** Matches visily App.tsx dashboard stat cards */
export type InterpreterSummaryCardVariant = 'open' | 'confirmed' | 'vos' | 'streak'

export type InterpreterSummaryCard = {
  id: string
  title: string
  value: string
  subtitle: string
  buttonLabel?: string
  buttonVariant?: 'outline' | 'solid'
  variant: InterpreterSummaryCardVariant
}

const TEXT_LABEL = '#565d6d'
const SHADOW_SM = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
const OUTLINE_BTN = '#a3a242'

const variantShell: Record<
  InterpreterSummaryCardVariant,
  { background: string; border: string; valueColor: string }
> = {
  open: {
    background: 'rgba(221, 220, 172, 0.2)',
    border: '1px solid rgba(222, 225, 230, 0.6)',
    valueColor: '#171a1f',
  },
  confirmed: {
    background: 'rgba(29, 215, 91, 0.1)',
    border: '1px solid rgba(222, 225, 230, 0.6)',
    valueColor: '#171a1f',
  },
  vos: {
    background: 'rgba(222, 59, 64, 0.05)',
    border: '1px solid rgba(222, 59, 64, 0.3)',
    valueColor: '#171a1f',
  },
  streak: {
    background: 'rgba(239, 176, 52, 0.1)',
    border: '1px solid rgba(222, 225, 230, 0.6)',
    valueColor: '#171a1f',
  },
}

function VosCornerBadge() {
  return (
    <div
      className='position-absolute d-flex align-items-center justify-content-center'
      style={{
        top: 12,
        right: 12,
        width: 32,
        height: 32,
        backgroundColor: 'rgba(222, 59, 64, 0.1)',
        borderRadius: 8,
      }}
      aria-hidden
    >
      <svg width={16} height={16} viewBox='0 0 24 24' fill='#de3b40' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.577 4.5-2.598 4.5H4.645c-2.021 0-3.752-2.5-2.598-4.5L9.401 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z'
        />
      </svg>
    </div>
  )
}

function StreakFlameIcon() {
  return (
    <svg width={44} height={44} viewBox='0 0 44 44' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path
        d='M22 8c-2 4-6 8-6 14 0 5 3 9 8 10-1-3 0-6 2-8 1 4 4 7 8 8 5-1 8-6 8-11 0-8-6-14-10-18-2 3-4 6-4 10 0-4-2-8-6-11z'
        fill='#efb034'
        fillOpacity={0.95}
      />
      <path
        d='M22 20c-1 2-3 4-3 7 0 3 2 5 5 6 0-2 1-3 2-4 0 2 2 4 4 5 3-1 5-4 5-7 0-5-4-8-6-11-1 2-2 4-2 6 0-2-1-4-3-6z'
        fill='#f5c84c'
      />
    </svg>
  )
}

const titleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.05em',
  color: TEXT_LABEL,
}

const valueStyle = (color: string): React.CSSProperties => ({
  fontFamily: "'Lexend', sans-serif",
  fontSize: 36,
  fontWeight: 400,
  lineHeight: 1.1,
  color,
})

const subtitleStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 11,
  color: TEXT_LABEL,
}

type InterpreterSummaryCardsProps = {
  cards: InterpreterSummaryCard[]
  /** Fired when a card action button is clicked (open / confirmed / vos cards). */
  onCardButtonClick?: (cardId: string) => void
}

export function InterpreterSummaryCards({ cards, onCardButtonClick }: InterpreterSummaryCardsProps) {
  return (
    <div className='row g-4 align-items-stretch'>
      {cards.map((card) => {
        const shell = variantShell[card.variant]

        const topBlock = (
          <>
            <p className='mb-0 text-uppercase' style={titleStyle}>
              {card.title}
            </p>

            {card.variant === 'streak' ? (
              <>
                <div className='d-flex align-items-center gap-2' style={{ marginTop: 24 }}>
                  <h2 className='mb-0' style={valueStyle(shell.valueColor)}>
                    {card.value}
                  </h2>
                  <StreakFlameIcon />
                </div>
                <p className='mb-0' style={subtitleStyle}>
                  {card.subtitle}
                </p>
              </>
            ) : (
              <>
                <h2 className='mb-0' style={{ ...valueStyle(shell.valueColor), marginTop: 24 }}>
                  {card.value}
                </h2>
                <p className='mb-0' style={subtitleStyle}>
                  {card.subtitle}
                </p>
              </>
            )}
          </>
        )

        const buttonVariant = card.buttonVariant ?? 'outline'

        const buttonEl = card.buttonLabel ? (
            <button
              type='button'
              className='w-100 border-0'
              onClick={() => onCardButtonClick?.(card.id)}
              style={
                buttonVariant === 'solid'
                  ? {
                      marginTop: 16,
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      backgroundColor: '#de3b40',
                      color: '#ffffff',
                    }
                  : {
                      marginTop: 16,
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      backgroundColor: '#ffffff',
                      color: OUTLINE_BTN,
                      border: `1px solid ${OUTLINE_BTN}`,
                    }
              }
              onMouseEnter={(e) => {
                if (buttonVariant === 'solid') {
                  e.currentTarget.style.backgroundColor = '#c53035'
                } else {
                  e.currentTarget.style.backgroundColor = 'rgba(163, 162, 66, 0.05)'
                }
              }}
              onMouseLeave={(e) => {
                if (buttonVariant === 'solid') {
                  e.currentTarget.style.backgroundColor = '#de3b40'
                } else {
                  e.currentTarget.style.backgroundColor = '#ffffff'
                }
              }}
            >
              {card.buttonLabel}
            </button>
          ) : null

        return (
          <div key={card.id} className='col-12 col-sm-6 col-xl-3 d-flex'>
            <div
              className='position-relative w-100 d-flex flex-column h-100'
              style={{
                backgroundColor: shell.background,
                border: shell.border,
                borderRadius: 12,
                padding: 20,
                boxShadow: SHADOW_SM,
              }}
            >
              {card.variant === 'vos' ? <VosCornerBadge /> : null}

              {topBlock}
              {buttonEl}
            </div>
          </div>
        )
      })}
    </div>
  )
}
